import api from "./api";

export type ManutencaoStatus =
  | "agendado"
  | "atendimento"
  | "concluido"
  | "cancelado"
  | "ausente"
  | "parcial"
  | "reagendado";

export interface ManutencaoFoto {
  id: string;
  storedFile: string;
  url: string;
  origem: "agendamento" | "servico" | "atividade";
  createdAt: string;
}

export interface ManutencaoAgendamento {
  id: string;
  cliente: string;
  telefone: string;
  casa: string;
  quadra: string;
  empreendimento: string;
  cidade: string;
  problemas: string[];
  observacao: string;
  data: string;
  hora: string;
  tecnico: string;
  tecnicos: string[];
  status: ManutencaoStatus;
  ocorrencia: number | null;
  ocTexto: string;
  fotos: ManutencaoFoto[];
  criadoPor: string;
  createdAt: string;
  updatedAt: string;
}

export interface ManutencaoAtividade {
  id: string;
  oficialNome: string;
  data: string;
  hora: string;
  observacao: string;
  fotos: ManutencaoFoto[];
  createdAt: string;
  updatedAt: string;
}

export interface ManutencaoColaborador {
  id: string;
  nome: string;
  email: string;
  cargo: string;
  status: "pendente" | "aprovado";
  valorDiaria: number;
  createdAt: string;
}

export interface ManutencaoEmpreendimento {
  id: string;
  nome: string;
  cidade: string;
  origem?: "sienge" | "local";
}

export interface ManutencaoCasa {
  id: string;
  empreendimentoId: string;
  numero: string;
  quadra: string;
  grupo: string;
  siengeUnitId?: string;
}

export interface ManutencaoProblema {
  id: string;
  categoria: string;
  item: string;
}

export type PontoMarca = "P" | "F" | "G";

export interface ManutencaoPonto {
  id: string;
  colaboradorNome: string;
  data: string;
  marca: PontoMarca;
}

export interface ManutencaoSnapshot {
  empreendimentos: ManutencaoEmpreendimento[];
  casas: ManutencaoCasa[];
  problemas: ManutencaoProblema[];
  colaboradores: ManutencaoColaborador[];
  agendamentos: ManutencaoAgendamento[];
  atividades: ManutencaoAtividade[];
  pontos: ManutencaoPonto[];
}

export async function fetchManutencao(): Promise<ManutencaoSnapshot> {
  const { data } = await api.get<ManutencaoSnapshot & { success: boolean }>("/api/manutencao");
  return data;
}

export async function createAgendamento(body: {
  cliente: string;
  telefone: string;
  casa: string;
  quadra: string;
  empreendimento: string;
  cidade: string;
  problemas: string[];
  observacao: string;
  data: string;
  hora: string;
  tecnicos: string[];
}): Promise<ManutencaoAgendamento> {
  const { data } = await api.post<{ agendamento: ManutencaoAgendamento }>("/api/manutencao/agendamentos", body);
  return data.agendamento;
}

export async function patchAgendamento(
  id: string,
  body: Partial<ManutencaoAgendamento>,
): Promise<ManutencaoAgendamento> {
  const { data } = await api.patch<{ agendamento: ManutencaoAgendamento }>(
    `/api/manutencao/agendamentos/${id}`,
    body,
  );
  return data.agendamento;
}

export async function removeAgendamento(id: string): Promise<void> {
  await api.delete(`/api/manutencao/agendamentos/${id}`);
}

export async function uploadAgendamentoFoto(
  id: string,
  file: File,
  origem: "agendamento" | "servico",
): Promise<void> {
  const form = new FormData();
  form.append("foto", file);
  form.append("origem", origem);
  await api.post(`/api/manutencao/agendamentos/${id}/fotos`, form);
}

export async function createAtividade(body: {
  oficialNome: string;
  data: string;
  hora: string;
  observacao: string;
}): Promise<ManutencaoAtividade> {
  const { data } = await api.post<{ atividade: ManutencaoAtividade }>("/api/manutencao/atividades", body);
  return data.atividade;
}

export async function removeAtividade(id: string): Promise<void> {
  await api.delete(`/api/manutencao/atividades/${id}`);
}

export async function uploadAtividadeFoto(id: string, file: File): Promise<void> {
  const form = new FormData();
  form.append("foto", file);
  await api.post(`/api/manutencao/atividades/${id}/fotos`, form);
}

export async function createEmpreendimento(nome: string, cidade: string): Promise<void> {
  await api.post("/api/manutencao/empreendimentos", { nome, cidade });
}

export async function linkCentroCusto(id: string, nome: string): Promise<void> {
  await api.post("/api/manutencao/centros-custo", { id, nome });
}

export async function unlinkCentroCusto(id: string): Promise<void> {
  await api.delete(`/api/manutencao/centros-custo/${encodeURIComponent(id)}`);
}

export async function saveDenominacoes(
  costCenterId: string,
  itens: Array<{ siengeUnitId: string; denominacao: string; quadra: string; grupo: string }>,
): Promise<void> {
  await api.put(`/api/manutencao/centros-custo/${encodeURIComponent(costCenterId)}/denominacoes`, { itens });
}

export async function createCasa(empreendimentoId: string, numero: string, quadra: string): Promise<void> {
  await api.post("/api/manutencao/casas", { empreendimentoId, numero, quadra });
}

export async function createProblema(categoria: string, item: string): Promise<void> {
  await api.post("/api/manutencao/problemas", { categoria, item });
}

export async function createColaborador(body: {
  nome: string;
  email: string;
  cargo: string;
  valorDiaria: number;
}): Promise<void> {
  await api.post("/api/manutencao/colaboradores", body);
}

export async function patchColaborador(
  id: string,
  body: Partial<Pick<ManutencaoColaborador, "nome" | "cargo" | "status" | "valorDiaria">>,
): Promise<void> {
  await api.patch(`/api/manutencao/colaboradores/${id}`, body);
}

export async function removeColaborador(id: string): Promise<void> {
  await api.delete(`/api/manutencao/colaboradores/${id}`);
}

export async function savePonto(
  colaboradorNome: string,
  data: string,
  marca: PontoMarca | null,
): Promise<void> {
  await api.put("/api/manutencao/ponto", { colaboradorNome, data, marca });
}

export function apiError(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
  return message || fallback;
}
